import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { apiJson, apiFetch } from '../apiClient';
import './SecurityPages.css';

const PER_PAGE_OPTIONS = [25, 50, 100];

function formatActionLabel(action) {
  if (!action) return '—';
  return String(action).replace(/_/g, ' ');
}

function truncate(str, max) {
  if (!str) return '—';
  const s = String(str);
  if (s.length <= max) return s;
  return `${s.slice(0, max - 1)}…`;
}

function locationLine(row) {
  const parts = [row.locationCity, row.locationCountry].filter(Boolean);
  return parts.length ? parts.join(', ') : '—';
}

const SecurityActivityLogPage = ({ onNavigate }) => {
  const [logs, setLogs] = useState([]);
  const [actions, setActions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);
  const [error, setError] = useState('');
  const [search, setSearch] = useState('');
  const [searchDebounced, setSearchDebounced] = useState('');
  const [actionFilter, setActionFilter] = useState('all');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(25);
  const [total, setTotal] = useState(0);
  const [lastPage, setLastPage] = useState(1);

  useEffect(() => {
    const t = setTimeout(() => setSearchDebounced(search.trim()), 350);
    return () => clearTimeout(t);
  }, [search]);

  useEffect(() => {
    setPage(1);
  }, [searchDebounced, actionFilter, dateFrom, dateTo, perPage]);

  const filterParams = useMemo(() => {
    const p = new URLSearchParams();
    if (searchDebounced) p.set('q', searchDebounced);
    if (actionFilter !== 'all') p.set('action', actionFilter);
    if (dateFrom) p.set('dateFrom', dateFrom);
    if (dateTo) p.set('dateTo', dateTo);
    return p;
  }, [searchDebounced, actionFilter, dateFrom, dateTo]);

  const queryString = useMemo(() => {
    const p = new URLSearchParams(filterParams);
    p.set('page', String(page));
    p.set('perPage', String(perPage));
    return p.toString();
  }, [filterParams, page, perPage]);

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const data = await apiJson(`/owner/activity-log?${queryString}`);
      setLogs(Array.isArray(data.logs) ? data.logs : []);
      setActions(Array.isArray(data.actions) ? data.actions : []);
      setTotal(typeof data.total === 'number' ? data.total : 0);
      setLastPage(typeof data.lastPage === 'number' ? data.lastPage : 1);
    } catch (e) {
      setLogs([]);
      setActions([]);
      setTotal(0);
      setLastPage(1);
      setError(e?.message || 'Could not load activity log.');
    } finally {
      setLoading(false);
    }
  }, [queryString]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    if (lastPage > 0 && page > lastPage) {
      setPage(lastPage);
    }
  }, [lastPage, page]);

  const hasFilters = search.trim() !== '' || actionFilter !== 'all' || dateFrom !== '' || dateTo !== '';

  const rangeStart = total === 0 ? 0 : (page - 1) * perPage + 1;
  const rangeEnd = total === 0 ? 0 : Math.min(page * perPage, total);

  const clearFilters = () => {
    setSearch('');
    setSearchDebounced('');
    setActionFilter('all');
    setDateFrom('');
    setDateTo('');
    setPage(1);
  };

  const exportCsv = async () => {
    setExporting(true);
    try {
      const p = new URLSearchParams(filterParams);
      p.set('format', 'csv');
      const res = await apiFetch(`/owner/activity-log?${p.toString()}`);
      if (!res.ok) {
        const errBody = await res.json().catch(() => ({}));
        throw new Error(errBody.message || res.statusText || 'Export failed');
      }
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `activity-log-${new Date().toISOString().slice(0, 19).replace(/[:T]/g, '-')}.csv`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      setError(e?.message || 'Could not export CSV.');
    } finally {
      setExporting(false);
    }
  };

  return (
    <div className="security-page security-page--log page-bleed">
      <div className="security-log-hero">
        <div className="security-page-head security-page-head--log">
          <button type="button" className="security-back security-back--on-dark" onClick={() => onNavigate?.('overview')}>
            ← Dashboard
          </button>
          <h1 className="security-title">Activity log</h1>
          <p className="security-lead security-lead--on-dark">
            Who did what, from where (IP and location), and when — including login outcomes, password changes, team
            updates, and product approvals.
          </p>
          <div className="security-log-hero-actions">
            <button type="button" className="security-refresh security-refresh--primary" onClick={load} disabled={loading}>
              {loading ? 'Loading…' : 'Refresh'}
            </button>
            <button
              type="button"
              className="security-log-export security-refresh security-refresh--primary"
              onClick={exportCsv}
              disabled={exporting || loading}
            >
              {exporting ? 'Exporting…' : 'Export CSV'}
            </button>
          </div>
        </div>
      </div>

      {error ? <div className="security-error security-error--banner">{error}</div> : null}

      <div className="security-log-body">
        <div className="security-log-toolbar">
          <label className="security-log-field security-log-field--grow">
            <span>Search</span>
            <input
              type="search"
              className="security-log-input"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Email, IP, action, location, device…"
              aria-label="Search activity log"
            />
          </label>
          <label className="security-log-field">
            <span>Action</span>
            <select
              className="security-log-select"
              value={actionFilter}
              onChange={(e) => setActionFilter(e.target.value)}
              aria-label="Filter by action"
            >
              <option value="all">All actions</option>
              {actions.map((a) => (
                <option key={a} value={a}>
                  {formatActionLabel(a)}
                </option>
              ))}
            </select>
          </label>
          <label className="security-log-field">
            <span>From date</span>
            <input
              type="date"
              className="security-log-input"
              value={dateFrom}
              onChange={(e) => setDateFrom(e.target.value)}
              aria-label="From date"
            />
          </label>
          <label className="security-log-field">
            <span>To date</span>
            <input
              type="date"
              className="security-log-input"
              value={dateTo}
              onChange={(e) => setDateTo(e.target.value)}
              aria-label="To date"
            />
          </label>
          <label className="security-log-field">
            <span>Rows per page</span>
            <select
              className="security-log-select"
              value={String(perPage)}
              onChange={(e) => setPerPage(Number(e.target.value))}
              aria-label="Rows per page"
            >
              {PER_PAGE_OPTIONS.map((n) => (
                <option key={n} value={String(n)}>
                  {n}
                </option>
              ))}
            </select>
          </label>
          {hasFilters ? (
            <button type="button" className="security-log-clear" onClick={clearFilters}>
              Clear filters
            </button>
          ) : null}
        </div>

        <p className="security-log-count">
          {total > 0 ? (
            <>
              Showing <strong>{rangeStart}</strong>–<strong>{rangeEnd}</strong> of <strong>{total}</strong> entries
              {hasFilters ? ' (filtered)' : ''} · Page <strong>{page}</strong> of <strong>{lastPage || 1}</strong>
            </>
          ) : (
            <>No entries match your criteria.</>
          )}
        </p>

        <div className="security-table-wrap security-table-wrap--grid">
          <table className="security-table security-table--soft security-table--activity">
            <thead>
              <tr>
                <th>When</th>
                <th>User (email)</th>
                <th>Action</th>
                <th>Details</th>
                <th>IP address</th>
                <th>Location</th>
                <th>Status</th>
                <th>Device / browser</th>
              </tr>
            </thead>
            <tbody>
              {loading && logs.length === 0 ? (
                <tr>
                  <td colSpan={8} className="security-empty">
                    Loading…
                  </td>
                </tr>
              ) : logs.length === 0 ? (
                <tr>
                  <td colSpan={8} className="security-empty">
                    {total === 0 && !hasFilters && !searchDebounced
                      ? 'No log entries yet. Actions will appear here as your team uses the system.'
                      : total === 0
                        ? 'No entries match your filters.'
                        : 'No rows on this page. Try another page or adjust filters.'}
                  </td>
                </tr>
              ) : (
                logs.map((row) => (
                  <tr key={row.id}>
                    <td className="security-td-time">{row.createdAt}</td>
                    <td className="security-td-who">{row.actorEmail || '—'}</td>
                    <td>
                      <code className="security-code security-code--cell" title={row.action}>
                        {formatActionLabel(row.action)}
                      </code>
                    </td>
                    <td className="security-td-detail">{row.summary || '—'}</td>
                    <td className="security-td-ip">{row.ipAddress || '—'}</td>
                    <td className="security-td-location">{locationLine(row)}</td>
                    <td>
                      <span
                        className={`security-status security-status--${
                          row.status === 'failed' ? 'failed' : row.status === 'warning' ? 'warning' : 'success'
                        }`}
                      >
                        {row.status === 'failed' ? 'Failed' : row.status === 'warning' ? 'Warning' : 'Success'}
                      </span>
                    </td>
                    <td className="security-td-device" title={row.userAgent || ''}>
                      {truncate(row.userAgent, 72)}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {total > 0 ? (
          <div className="security-log-pagination">
            <button type="button" className="security-log-pagebtn" disabled={page <= 1 || loading} onClick={() => setPage((p) => Math.max(1, p - 1))}>
              Previous
            </button>
            <span className="security-log-pageinfo">
              Page {page} / {lastPage || 1}
            </span>
            <button
              type="button"
              className="security-log-pagebtn"
              disabled={page >= lastPage || loading}
              onClick={() => setPage((p) => p + 1)}
            >
              Next
            </button>
          </div>
        ) : null}
      </div>
    </div>
  );
};

export default SecurityActivityLogPage;
